import axios from "axios";
import React, { useState } from "react";
import { Grid, Divider, IconButton } from "@material-ui/core";
import { Box, Typography, Button, useMediaQuery } from "@material-ui/core";
// import
import { InputField } from "components";
import FindMyCity from "components/FindMyCity";
import FieldErrorMessage from "components/FormikError";
import { ReactComponent as Edit } from "assets/icons/EditReport.svg";
import { ReactComponent as Success } from "assets/icons/PaidIcon.svg";
import { postalCodeLabel, stateLabel } from "helper/postalCodeFormat";
import { ReactComponent as Delete } from "assets/icons/DeleteBlue.svg";
// direct import
import "../style.scss";
import { MyCheckboxRenewal } from "../checkbox";
import { Fonts, FontColors } from "theme/fontConfig";

const AccountInfo = ({ formik }) => {
  const stateTitle = stateLabel();
  const postalLabel = postalCodeLabel();
  const isSmallScreen = useMediaQuery("(max-width:750px)");
  const [editingIndex, setEditingIndex] = useState(null);
  const [backupAddress, setBackupAddress] = useState(null);

  const addressHandleChange = async (address, index) => {
    formik.setFieldValue(`accountInfo.addresses[${index}].location`, {
      formattedAddress: address?.label,
      place_id: address?.value?.place_id || "",
    });

    const result = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          place_id: address?.value?.place_id,
          key: "AIzaSyCAt0TjvU_m8ufUMYn2EXi9mteHSOUB7uA",
        },
      }
    );

    let zip_code = result.data.results[0]?.address_components?.filter(
      (item) => item.types[0] === "postal_code"
    );
    formik.setFieldValue(
      `accountInfo.addresses[${index}].zip_code`,
      zip_code?.[0]?.short_name
    );

    const sortedAddress = address?.label?.split(",") || [];
    if (sortedAddress.length === 2) {
      formik.setFieldValue(
        `accountInfo.addresses[${index}].state`,
        sortedAddress.at(-1) || ""
      );
      formik.setFieldValue(
        `accountInfo.addresses[${index}].city`,
        sortedAddress.at(-2) || ""
      );
    } else {
      formik.setFieldValue(
        `accountInfo.addresses[${index}].state`,
        sortedAddress.at(-2) || ""
      );
      formik.setFieldValue(
        `accountInfo.addresses[${index}].city`,
        sortedAddress.at(-3) || ""
      );
    }
  };

  const checkboxFields = [
    { label: "Active:", name: "active" },
    { label: "Billing:", name: "billing" },
  ];

  const handleAddAddress = () => {
    const checkLength = formik?.values?.accountInfo?.addresses?.length === 0;
    const newAddress = {
      location: { formattedAddress: "", place_id: "" },
      active: checkLength ? true : false,
      billing: checkLength ? true : false,
      applyAddressToContacts: false,
      zip_code: "",
      country: "",
      state: "",
      city: "",
      addressLine2: "",
      edit: true,
      isNew: true,
    };

    const updatedAddresses = [
      ...(formik.values.accountInfo.addresses || []),
      newAddress,
    ];
    formik.setFieldValue("accountInfo.addresses", updatedAddresses);
    setEditingIndex(updatedAddresses.length - 1);
  };

  const handleApprove = (index) => {
    const updatedAddresses = [...(formik.values.accountInfo.addresses || [])];
    updatedAddresses[index] = {
      ...updatedAddresses[index],
      approve: true,
      edit: true,
    };
    formik.setFieldValue("accountInfo.addresses", updatedAddresses);
    setEditingIndex(null);
  };

  const handleEdit = (index) => {
    setBackupAddress({ ...formik.values.accountInfo.addresses[index] });
    setEditingIndex(index);
  };

  const handleUpdate = (index) => {
    const memberPath = `accountInfo.addresses[${index}]`;
    const address = formik.values.accountInfo.addresses[index];
    const errors = {};

    // --- VALIDATION RULES ---
    if (
      !address.location?.formattedAddress ||
      address.location.formattedAddress.trim() === ""
    ) {
      errors["location.formattedAddress"] = "Address is required";
    }
    if (!address.city || address.city.trim() === "") {
      errors.city = "City is required";
    }
    if (!address.state || address.state.trim() === "") {
      errors.state = "State is required";
    }

    // --- If errors exist, show them and stop update ---
    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach((key) => {
        formik.setFieldTouched(`${memberPath}.${key}`, true, false);
        formik.setFieldError(`${memberPath}.${key}`, errors[key]);
      });
      return;
    }

    // --- Clear previous field errors ---
    formik.setFieldError(`${memberPath}.location.formattedAddress`, "");
    formik.setFieldError(`${memberPath}.city`, "");
    formik.setFieldError(`${memberPath}.state`, "");

    // --- Enforce one active & billing address ---
    const updatedAddresses = [...(formik.values.accountInfo.addresses || [])];
    const hasActive = updatedAddresses.some((addr) => addr.active);
    const hasBilling = updatedAddresses.some((addr) => addr.billing);

    if (!hasActive) {
      updatedAddresses[0].active = true;
    }
    if (!hasBilling) {
      updatedAddresses[0].billing = true;
    }

    updatedAddresses[index].edit = false;
    updatedAddresses[index].isNew = false;

    formik.setFieldValue("accountInfo.addresses", updatedAddresses);
    setEditingIndex(null);
    setBackupAddress(null);
  };

  const handleCancel = (index) => {
    const updatedAddresses = [...(formik.values.accountInfo.addresses || [])];

    if (updatedAddresses[index]?.isNew) {
      updatedAddresses.splice(index, 1);
    } else {
      updatedAddresses[index] = backupAddress;
    }

    const hasActiveBilling = updatedAddresses.some(
      (addr) => addr.active && addr.billing
    );

    if (!hasActiveBilling && updatedAddresses.length > 0) {
      updatedAddresses[0].active = true;
      updatedAddresses[0].billing = true;
    }

    formik.setFieldValue("accountInfo.addresses", updatedAddresses);
    setEditingIndex(null);
    setBackupAddress(null);
  };

  const handleDelete = (index) => {
    const addresses = [...(formik.values.accountInfo.addresses || [])];

    if (addresses.length <= 1) {
      alert("At least one address must remain.");
      return;
    }

    const remainingAddresses = addresses.filter((_, i) => i !== index);

    const hasActiveBilling = remainingAddresses.some(
      (addr) => addr.active && addr.billing
    );

    if (!hasActiveBilling && remainingAddresses.length > 0) {
      remainingAddresses[0].active = true;
      remainingAddresses[0].billing = true;
    }

    formik.setFieldValue("accountInfo.addresses", remainingAddresses);
    setEditingIndex(null);
    setBackupAddress(null);
  };

  return (
    <Box
      className="summary-renewal__card account-info"
      sx={{ paddingBottom: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: isSmallScreen ? "normal" : "baseline",
          justifyContent: "space-between",
          flexDirection: isSmallScreen ? "column" : "row",
        }}
      >
        <Typography variant="h6" className="title_address_details">
          Validate Account Address
        </Typography>
        <Button
          color="primary"
          onClick={handleAddAddress}
          disabled={editingIndex !== null}
          style={{
            opacity: editingIndex !== null ? 0.5 : 1,
            marginBottom: isSmallScreen ? 15 : 0,
          }}
        >
          <Box className="account-info-labels">+ Account Address</Box>
        </Button>
      </Box>

      <Divider className="account-info__divider" />

      {formik.values?.accountInfo?.addresses?.length === 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt={5}
          mb={5}
        >
          <Typography>No account addresses found.</Typography>
        </Box>
      )}

      {(formik.values?.accountInfo?.addresses || []).map((address, index) => (
        <Box
          key={address?._id || index}
          className="account-info__row"
          mb={2}
          style={{ position: "relative" }}
        >
          <Grid container spacing={3}>
            {editingIndex !== index && (
              <Grid item xs={12}>
                {!address?.approve && (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <IconButton
                      onClick={() => handleEdit(index)}
                      disabled={editingIndex !== null}
                      style={{
                        width: 47,
                        height: 38,
                        opacity: editingIndex !== null ? 0.5 : 1,
                      }}
                    >
                      <Edit style={{ width: 40, height: 40 }} />
                    </IconButton>
                    |
                    <Button
                      onClick={() => handleApprove(index)}
                      disabled={editingIndex !== null}
                      style={{ opacity: editingIndex !== null ? 0.5 : 1 }}
                    >
                      <Box className="account-info-labels">Approve</Box>
                    </Button>
                  </Box>
                )}
                {address?.approve && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      paddingRight: 15,
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => handleEdit(index)}
                      style={{ width: 47, height: 38 }}
                    >
                      <Edit style={{ width: 40, height: 40 }} />
                    </IconButton>
                    |
                    <Success
                      style={{ height: 25, width: 25, marginLeft: 10 }}
                    />
                  </Box>
                )}
              </Grid>
            )}
            {editingIndex === index && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                    justifyContent: "space-between",
                    height: "100%",
                  }}
                >
                  <Box sx={{ color: FontColors.Blue2555EF }}>
                    <Button onClick={() => handleCancel(index)}>
                      <Box className="account-info-labels">Cancel</Box>
                    </Button>
                    |
                    <Button onClick={() => handleUpdate(index)}>
                      <Box className="account-info-labels">Update</Box>
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FindMyCity
                handleChange={(value) => addressHandleChange(value, index)}
                title={
                  <>
                    Address<span style={{ color: FontColors.Red }}>*</span>
                  </>
                }
                value={{
                  label: address?.location?.formattedAddress || "",
                  value: address?.location?.place_id || "",
                }}
                saveNewAddress={true}
                isDisabled={editingIndex !== index}
              />
              <FieldErrorMessage
                formik={formik}
                name={`accountInfo.addresses[${index}].location.formattedAddress`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField
                formik={formik}
                title="Address Line 2 (Optional)"
                name={`accountInfo.addresses[${index}].addressLine2`}
                placeholder="Apt, suite, unit, floor, company"
                value={address?.addressLine2 || ""}
                disabled={editingIndex !== index}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField
                formik={formik}
                title="City"
                name={`accountInfo.addresses[${index}].city`}
                placeholder="New York"
                value={address?.city || ""}
                formStyle={{ marginBottom: 0 }}
                disabled={editingIndex !== index}
                mandatory
              />
              <FieldErrorMessage
                formik={formik}
                name={`accountInfo.addresses[${index}].city`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField
                formik={formik}
                title={stateTitle}
                name={`accountInfo.addresses[${index}].state`}
                placeholder="NY"
                value={address?.state || ""}
                formStyle={{ marginBottom: 0 }}
                disabled={editingIndex !== index}
                mandatory
              />
              <FieldErrorMessage
                formik={formik}
                name={`accountInfo.addresses[${index}].state`}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputField
                formik={formik}
                title={postalLabel}
                name={`accountInfo.addresses[${index}].zip_code`}
                placeholder="Zip Code"
                value={address?.zip_code || ""}
                formStyle={{ marginBottom: 0 }}
                disabled={editingIndex !== index}
              />
              <FieldErrorMessage
                formik={formik}
                name={`accountInfo.addresses[${index}].zip_code`}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputField
                formik={formik}
                title="Neighborhood"
                name={`accountInfo.addresses[${index}].neighborhood`}
                placeholder="Neighborhood (if applicable)"
                value={address?.neighborhood || ""}
                disabled={editingIndex !== index}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputField
                formik={formik}
                title="Country"
                name={`accountInfo.addresses[${index}].country`}
                placeholder="Country"
                value={address?.country || ""}
                formStyle={{ marginBottom: 0 }}
                disabled={editingIndex !== index}
              />
              <FieldErrorMessage
                formik={formik}
                name={`accountInfo.addresses[${index}].country`}
              />
            </Grid>

            {checkboxFields.map((field, idx) => (
              <Grid item xs={12} sm={3} key={idx}>
                <Typography
                  className="account-info__label"
                  sx={{ fontSize: Fonts.GeneralLabelFont.fontSize }}
                >
                  {field.label}
                </Typography>
                <MyCheckboxRenewal
                  color="primary"
                  checked={address?.[field.name] || false}
                  name={`accountInfo.addresses[${index}].${field.name}`}
                  onChange={(e) => {
                    const updatedAddresses = [
                      ...formik.values.accountInfo.addresses,
                    ];

                    if (e.target.checked) {
                      updatedAddresses.forEach((addr, i) => {
                        addr[field.name] = i === index;
                      });
                    } else {
                      const isOnlyChecked =
                        updatedAddresses.filter((addr) => addr[field.name])
                          .length === 1;

                      if (isOnlyChecked) {
                        return;
                      }

                      updatedAddresses[index][field.name] = false;
                    }

                    formik.setFieldValue(
                      "accountInfo.addresses",
                      updatedAddresses
                    );
                  }}
                  disabled={editingIndex !== index}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {editingIndex !== index && !address?.approve && (
                  <IconButton onClick={() => handleDelete(index)}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
          {index > 0 && (
            <Divider
              className="account-info__divider"
              style={{ width: "100%", position: "absolute", top: "-18px" }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default AccountInfo;
